import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client.ts';
import type { GenerationResponse } from '../bindings/GenerationResponse.ts';
import type { StartGenerationResponse } from '../bindings/StartGenerationResponse.ts';
import type { BlockResponse } from '../bindings/BlockResponse.ts';
import type { PlanDoc } from '../bindings/PlanDoc.ts';

export function useStartGeneration(planId: string) {
  return useMutation({
    mutationFn: (blockId: string) =>
      apiFetch<StartGenerationResponse>(`/v1/plans/${planId}/blocks/${blockId}/generate`, {
        method: 'POST',
      }),
  });
}

/** Polls while the generation is pending. */
export function useGeneration(planId: string, generationId: string | undefined) {
  return useQuery({
    queryKey: ['generation', planId, generationId],
    queryFn: () =>
      apiFetch<GenerationResponse>(`/v1/plans/${planId}/generations/${generationId}`),
    enabled: !!generationId,
    refetchInterval: (query) =>
      query.state.data?.generation.status === 'pending' ? 1500 : false,
  });
}

export function useApplyGeneration(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (generationId: string) =>
      apiFetch<BlockResponse>(`/v1/plans/${planId}/generations/${generationId}/apply`, {
        method: 'POST',
      }),
    onSuccess: (res) => {
      queryClient.setQueryData<PlanDoc>(['plan', planId], (doc) =>
        doc
          ? {
              ...doc,
              sections: doc.sections.map((s) => ({
                ...s,
                blocks: s.blocks.map((b) => (b.id === res.block.id ? res.block : b)),
              })),
            }
          : doc,
      );
    },
  });
}

export function useDiscardGeneration(planId: string) {
  return useMutation({
    mutationFn: (generationId: string) =>
      apiFetch<void>(`/v1/plans/${planId}/generations/${generationId}/discard`, {
        method: 'POST',
      }),
  });
}
